import { useCallback, useReducer, useRef } from 'react';
import { useCreateBriefTemplate } from '@/entities/brief-template';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { notificationService } from '@/lib/services/notification.service';
import { logger } from '@/lib/utils/logger';
import { i18n } from '@/shared/i18n';
import { matchPipelineStep } from '../../lib/pipeline-steps';
import type { GeneratedTemplate } from '../../lib/parse-generated-template';
import { uploadIntakeFiles, type IntakeUploadedAsset } from '../../lib/upload-intake-files';
import type { BriefTemplateSubmitPayload } from '../brief-template-intake.types';
import { PIPELINE_STEPS, TEMPLATE_GENERATION_AGENT_ID } from '../template-generation.constants';
import type {
  GenerationStatus,
  PipelineStep,
  PipelineStepId,
  PipelineStepState,
} from '../template-generation.types';

type StepsMap = Record<PipelineStepId, PipelineStepState>;

interface GeneratedTemplateResult {
  template: GeneratedTemplate;
}

interface State {
  status: GenerationStatus;
  steps: StepsMap;
  error: Error | null;
  templateId: string | null;
  payload: BriefTemplateSubmitPayload | null;
  uploadedAssets: IntakeUploadedAsset[];
  generatedTemplate: GeneratedTemplateResult | null;
}

type Action =
  | { type: 'start'; payload: BriefTemplateSubmitPayload }
  | { type: 'uploads-done'; assets: IntakeUploadedAsset[] }
  | { type: 'step-running'; id: PipelineStepId }
  | { type: 'step-done'; id: PipelineStepId }
  | { type: 'step-error'; id: PipelineStepId }
  | { type: 'template-ready'; result: GeneratedTemplateResult }
  | { type: 'persist-success'; templateId: string }
  | { type: 'persist-error'; error: Error }
  | { type: 'flow-error'; error: Error }
  | { type: 'reset' };

const ALL_PENDING_STEPS: StepsMap = PIPELINE_STEPS.reduce<StepsMap>((acc, step) => {
  acc[step.id] = 'pending';
  return acc;
}, {} as StepsMap);

const INITIAL_STATE: State = {
  status: 'idle',
  steps: ALL_PENDING_STEPS,
  error: null,
  templateId: null,
  payload: null,
  uploadedAssets: [],
  generatedTemplate: null,
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'start':
      return { ...INITIAL_STATE, status: 'uploading', payload: action.payload };
    case 'uploads-done':
      return { ...state, uploadedAssets: action.assets, status: 'streaming' };
    /* eslint-disable i18next/no-literal-string -- step status values */
    case 'step-running':
      return { ...state, steps: { ...state.steps, [action.id]: 'running' } };
    case 'step-done':
      return { ...state, steps: { ...state.steps, [action.id]: 'done' } };
    case 'step-error':
      return {
        ...state,
        steps: { ...state.steps, [action.id]: 'error' },
        status: 'error',
      };
    /* eslint-enable i18next/no-literal-string */
    case 'template-ready':
      return {
        ...state,
        steps: {
          ...state.steps,
          // Closed here rather than on its own node: the workflow skips the extraction step when
          // the user attached nothing, so that step never reports and would sit pending forever.
          'reading-files': 'done',
          'generating-sections': 'done',
          'saving-template': 'running',
        },
        status: 'persisting',
        generatedTemplate: action.result,
      };
    case 'persist-success':
      return {
        ...state,
        steps: { ...state.steps, 'saving-template': 'done' },
        status: 'success',
        templateId: action.templateId,
      };
    case 'persist-error':
      return {
        ...state,
        steps: { ...state.steps, 'saving-template': 'error' },
        status: 'error',
        error: action.error,
      };
    case 'flow-error':
      return { ...state, status: 'error', error: action.error };
    case 'reset':
      return INITIAL_STATE;
  }
};

export interface UseTemplateGenerationOptions {
  onSuccess?: (templateId: string) => void;
}

export interface TemplateGenerationStreamProps {
  agentId: string;
  workspaceId: string;
  payload: BriefTemplateSubmitPayload;
  uploadedAssets: IntakeUploadedAsset[];
  abortSignal: AbortSignal;
  onStepEvent: (kind: 'start' | 'complete' | 'error', label: string) => void;
  onTemplateReady: (result: GeneratedTemplateResult) => void;
  onStreamError: (error: Error) => void;
}

export interface UseTemplateGenerationReturn {
  status: GenerationStatus;
  steps: (PipelineStep & { state: PipelineStepState })[];
  error: Error | null;
  templateId: string | null;
  start: (payload: BriefTemplateSubmitPayload) => Promise<void>;
  cancel: () => void;
  retry: () => void;
  reset: () => void;
  streamProps: TemplateGenerationStreamProps | null;
}

export const useTemplateGeneration = ({
  onSuccess,
}: UseTemplateGenerationOptions): UseTemplateGenerationReturn => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const abortRef = useRef<AbortController | null>(null);
  const workspaceId = useCurrentWorkspaceId();
  const mutation = useCreateBriefTemplate();

  const chosenLevel = state.payload?.level;

  const fireMutate = useCallback(
    (result: GeneratedTemplateResult) => {
      const { template } = result;
      mutation.mutate(
        {
          name: template.name,
          ...(chosenLevel ? { level: chosenLevel } : {}),
          ...(template.description ? { description: template.description } : {}),
          ...(template.sections.length > 0
            ? {
                sections: template.sections.map((s) => ({
                  name: s.name,
                  position: s.position,
                  required: s.required,
                  aiAllowed: s.aiAllowed,
                  instructions: s.instructions ?? null,
                })),
              }
            : {}),
        },
        {
          onSuccess: (persisted) => {
            dispatch({ type: 'persist-success', templateId: persisted.id });
            onSuccess?.(persisted.id);
          },
          onError: (error) => {
            dispatch({ type: 'persist-error', error });
          },
        },
      );
    },
    [mutation, onSuccess, chosenLevel],
  );

  const start = useCallback(async (payload: BriefTemplateSubmitPayload) => {
    const controller = new AbortController();
    abortRef.current = controller;
    dispatch({ type: 'start', payload });

    try {
      const uploadResults = await uploadIntakeFiles(payload.files, controller.signal);

      if (controller.signal.aborted) return;

      const successfulAssets = uploadResults.flatMap((r) => (r.asset ? [r.asset] : []));
      const allFailed = payload.files.length > 0 && successfulAssets.length === 0;
      if (allFailed) {
        const error = new Error('All file uploads failed');
        dispatch({ type: 'flow-error', error });
        notificationService.showError(
          i18n.t('briefs:briefTemplateCreation.useTemplateGeneration.failedToUploadFilesPlease'),
        );
        return;
      }

      dispatch({ type: 'uploads-done', assets: successfulAssets });
    } catch (cause) {
      const error = cause instanceof Error ? cause : new Error(String(cause));
      if (error.name === 'AbortError') return;
      dispatch({ type: 'flow-error', error });
      notificationService.showError(
        error.message ||
          i18n.t(
            'briefs:briefTemplateCreation.useTemplateGeneration.failedToStartTemplateGeneration',
          ),
      );
    }
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    dispatch({ type: 'reset' });
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    dispatch({ type: 'reset' });
    mutation.reset();
  }, [mutation]);

  const retry = useCallback(() => {
    if (!state.generatedTemplate) return;
    fireMutate(state.generatedTemplate);
  }, [state.generatedTemplate, fireMutate]);

  const handleStepEvent = useCallback((kind: 'start' | 'complete' | 'error', label: string) => {
    const id = matchPipelineStep(label);
    if (!id) {
      logger.debug('useTemplateGeneration: unknown pipeline step label', { label });
      return;
    }
    if (kind === 'start') {
      dispatch({ type: 'step-running', id });
    } else if (kind === 'complete') {
      dispatch({ type: 'step-done', id });
    } else {
      dispatch({ type: 'step-error', id });
    }
  }, []);

  const handleTemplateReady = useCallback(
    (result: GeneratedTemplateResult) => {
      dispatch({ type: 'template-ready', result });
      fireMutate(result);
    },
    [fireMutate],
  );

  const handleStreamError = useCallback((error: Error) => {
    dispatch({ type: 'flow-error', error });
    notificationService.showError(
      error.message ||
        i18n.t('briefs:briefTemplateCreation.useTemplateGeneration.templateGenerationStreamFailed'),
    );
  }, []);

  const steps = PIPELINE_STEPS.map((step) => ({
    ...step,
    state: state.steps[step.id],
  }));

  const streamProps: TemplateGenerationStreamProps | null =
    state.status === 'streaming' &&
    state.payload !== null &&
    abortRef.current !== null &&
    workspaceId !== null
      ? {
          agentId: TEMPLATE_GENERATION_AGENT_ID,
          workspaceId,
          payload: state.payload,
          uploadedAssets: state.uploadedAssets,
          abortSignal: abortRef.current.signal,
          onStepEvent: handleStepEvent,
          onTemplateReady: handleTemplateReady,
          onStreamError: handleStreamError,
        }
      : null;

  return {
    status: state.status,
    steps,
    error: state.error,
    templateId: state.templateId,
    start,
    cancel,
    retry,
    reset,
    streamProps,
  };
};
